import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CheckVerified02,
  Edit03,
  Eye,
  Globe01,
  Link01,
  LinkBroken01,
  Mail01,
  MessageTextSquare02,
  Phone,
  Plus,
  RefreshCcw01,
  RefreshCw01,
  Rows01,
  Send03,
  Trash01,
  XCircle,
  Zap,
} from '@untitledui/icons';

interface EventTypeIconProps {
  eventTypeName: string;
  className?: string;
}

/**
 * Centralized icon component for CRM timeline event types.
 * Renders the appropriate icon based on event type name.
 *
 * Standard mappings:
 * - ahoy_object_created → Plus (simple plus sign)
 * - ahoy_object_deleted → Trash01
 * - ahoy_property_updated → Edit03
 * - ahoy_association_added → Link01
 * - ahoy_association_removed → LinkBroken01
 * - ahoy_email_sent → Send03
 * - ahoy_email_received → Mail01
 * - ahoy_web_page_viewed → Globe01
 * - ahoy_errored → AlertCircle
 * - ahoy_call_completed / ahoy_call_missed / ahoy_voicemail_received → Phone
 * - ahoy_sms_sent / ahoy_sms_received → MessageTextSquare02
 * - ahoy_invoice_paid → CheckCircle
 * - ahoy_invoice_payment_failed → XCircle
 * - ahoy_invoice_refunded → RefreshCcw01
 *
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon utility function co-located with icon component
export function getEventTypeIconComponent(eventTypeName: string) {
  switch (eventTypeName) {
    case 'ahoy_object_created':
      return Plus;
    case 'ahoy_object_deleted':
      return Trash01;
    case 'ahoy_object_restored':
      return RefreshCw01;
    case 'ahoy_property_updated':
      return Edit03;
    case 'ahoy_association_added':
      return Link01;
    case 'ahoy_association_removed':
      return LinkBroken01;
    case 'ahoy_email_sent':
      return Send03;
    case 'ahoy_email_received':
      return Mail01;
    case 'ahoy_email_opened':
      return Eye;
    case 'ahoy_web_form_submitted':
      return Rows01;
    case 'ahoy_web_page_viewed':
      return Globe01;
    case 'ahoy_errored':
      return AlertCircle;
    case 'ahoy_call_completed':
    case 'ahoy_call_missed':
    case 'ahoy_voicemail_received':
      return Phone;
    case 'ahoy_sms_sent':
    case 'ahoy_sms_received':
      return MessageTextSquare02;
    case 'ahoy_event_processed': // deprecated predecessor — legacy rows still render
    case 'ahoy_automation_ran':
      // Automation run marker — match the ahoy_event_automation entity icon (Zap)
      // rather than falling through to the default Plus ("add") glyph.
      return Zap;
    case 'ahoy_invoice_paid':
      return CheckCircle;
    case 'ahoy_invoice_payment_failed':
      return XCircle;
    case 'ahoy_invoice_refunded':
      return RefreshCcw01;
    case 'ahoy_agent_ran':
      // Verified-check: an agent's autonomy run, distinct from the
      // plain CheckCircle used for payment success.
      return CheckVerified02;
    case 'ahoy_outbound_requested':
      return Send03;
    case 'ahoy_outbound_attempt_claimed':
      return Send03;
    case 'ahoy_outbound_dispatched':
      return CheckCircle;
    case 'ahoy_outbound_failed':
      return XCircle;
    case 'ahoy_outbound_attempt_failed':
      return AlertTriangle;
    case 'ahoy_outbound_replay_requested':
      return RefreshCw01;
    default:
      return Plus;
  }
}

export function EventTypeIcon({
  eventTypeName,
  className,
}: EventTypeIconProps) {
  const Icon = getEventTypeIconComponent(eventTypeName);
  return <Icon className={className} />;
}
